import { useId, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { MODES, formatZoom, type Mode, type View } from './model';

export interface FrameInfo {
  width: number;
  height: number;
  ms: number;
}

export interface FractalActions {
  mode(value: Mode): void;
  zoomIn(): void;
  zoomOut(): void;
  reset(): void;
  maxIter(value: number): void;
  hue(value: number): void;
}

interface FractalShellProps {
  view: View;
  zoom: number;
  status: 'loading' | 'ready' | 'error';
  error?: string | null;
  frame?: FrameInfo | null;
  engaged?: boolean;
  wheelHint?: boolean;
  rootRef?: Ref<HTMLDivElement>;
  wrapRef?: Ref<HTMLDivElement>;
  canvasRef?: Ref<HTMLCanvasElement>;
  actions?: FractalActions;
}

const HUE_STEPS = 628;

export const FractalShell = ({
  view,
  zoom,
  status,
  error,
  frame,
  engaged = false,
  wheelHint = false,
  rootRef,
  wrapRef,
  canvasRef,
  actions,
}: FractalShellProps) => {
  const { t } = useTranslation();
  const en = (key: string) => t(`caseStudy.fractal.${key}`, { lng: 'en' });
  const id = useId();
  const disabled = !actions || status !== 'ready';

  return (
    <div ref={rootRef}>
      <div className={`tile ${engaged ? 'tile-engaged' : ''}`}>
        <div ref={wrapRef} className="tile-in relative aspect-[4/3] !bg-black !p-0 sm:aspect-[5/3]">
          <canvas
            ref={canvasRef}
            tabIndex={actions ? 0 : -1}
            role="img"
            aria-label={en('canvasLabel')}
            aria-describedby={`${id}-hint`}
            className={`block h-full w-full cursor-grab select-none outline-none active:cursor-grabbing ${
              engaged ? 'touch-none' : 'touch-pan-y'
            }`}
          />
          {status === 'loading' && (
            <div className="absolute inset-0 grid place-items-center font-mono text-xs text-white/70">
              {en('loading')}
            </div>
          )}
          <noscript>
            <div className="absolute inset-0 grid place-items-center bg-black px-6 text-center font-mono text-xs text-white/80">
              {en('noscript')}
            </div>
          </noscript>
          {status === 'error' && (
            <div
              role="alert"
              className="absolute inset-0 grid place-items-center px-6 text-center font-mono text-xs text-white/85"
            >
              {en('error')}: {error}
            </div>
          )}
          {status === 'ready' && (
            <p
              aria-live="off"
              className="pointer-events-none absolute left-3 top-3 bg-black/75 px-2 py-1 font-mono text-[0.68rem] text-white/85"
            >
              {formatZoom(zoom)}× zoom
              {frame ? ` · ${frame.width}×${frame.height} px · ${frame.ms.toFixed(frame.ms < 10 ? 1 : 0)} ms` : ''}
            </p>
          )}
          {wheelHint && (
            <p
              role="status"
              className="pointer-events-none absolute inset-x-0 bottom-4 mx-auto w-fit max-w-[90%] bg-black/85 px-3 py-1.5 text-center font-mono text-[0.7rem] text-white"
            >
              {en('wheelHint')}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div role="group" aria-label={en('mode')} className="flex gap-2">
          {MODES.map((m) => (
            <button
              key={m}
              type="button"
              disabled={disabled}
              aria-pressed={view.mode === m}
              onClick={() => actions?.mode(m)}
              className={`${view.mode === m ? 'btn-primary' : 'btn-outline'} min-h-10 justify-center !px-3.5 !py-0 disabled:opacity-60`}
            >
              {en(m)}
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            disabled={disabled}
            aria-label={en('zoomOut')}
            onClick={() => actions?.zoomOut()}
            className="btn-outline h-10 w-10 justify-center !p-0 text-base disabled:opacity-60"
          >
            <span aria-hidden="true">−</span>
          </button>
          <button
            type="button"
            disabled={disabled}
            aria-label={en('zoomIn')}
            onClick={() => actions?.zoomIn()}
            className="btn-outline h-10 w-10 justify-center !p-0 text-base disabled:opacity-60"
          >
            <span aria-hidden="true">+</span>
          </button>
          <button
            type="button"
            disabled={disabled}
            aria-label={en('reset')}
            title={en('reset')}
            onClick={() => actions?.reset()}
            className="btn-outline h-10 w-10 justify-center !p-0 text-base disabled:opacity-60"
          >
            <span aria-hidden="true">↺</span>
          </button>
        </div>
      </div>

      <div className="mt-2 grid gap-x-8 sm:grid-cols-2">
        <div className="flex min-h-10 items-center gap-3 font-mono text-[0.7rem] text-muted-foreground">
          <label htmlFor={`${id}-detail`} className="w-12 shrink-0">
            {en('detail')}
          </label>
          <input
            id={`${id}-detail`}
            type="range"
            min={50}
            max={1000}
            step={10}
            disabled={disabled}
            value={view.maxIter}
            onChange={(e) => actions?.maxIter(Number(e.target.value))}
            className="range min-w-0 flex-1"
          />
          <output htmlFor={`${id}-detail`} className="w-10 shrink-0 text-right tabular-nums text-foreground">
            {view.maxIter}
          </output>
        </div>
        <div className="flex min-h-10 items-center gap-3 font-mono text-[0.7rem] text-muted-foreground">
          <label htmlFor={`${id}-hue`} className="w-12 shrink-0">
            {en('hue')}
          </label>
          <input
            id={`${id}-hue`}
            type="range"
            min={0}
            max={HUE_STEPS}
            step={1}
            disabled={disabled}
            value={Math.round(view.hue * 100)}
            onChange={(e) => actions?.hue(Number(e.target.value) / 100)}
            className="range min-w-0 flex-1"
          />
          <output htmlFor={`${id}-hue`} className="w-10 shrink-0 text-right tabular-nums text-foreground">
            {Math.round((view.hue * 180) / Math.PI)}°
          </output>
        </div>
      </div>

      <p id={`${id}-hint`} className="mt-2 font-mono text-[0.7rem] text-muted-foreground">
        <span className="pointer-coarse:hidden">{en(engaged ? 'hintFineEngaged' : 'hintFine')}</span>
        <span className="hidden pointer-coarse:inline">{en(engaged ? 'hintCoarseEngaged' : 'hintCoarse')}</span>
      </p>
    </div>
  );
};
